import { merchantKey } from './recurring.ts';

// Reconciling a bank download with what is already stored (BANKING.md §2.4). Pure: the server
// feeds it one account's transactions and applies the plan in one database transaction.
//
// Order of matching for each downloaded transaction:
//   1. the same provider id                 → update the stored one (fields the owner changed stay);
//      an id of an entry the owner deleted  → skip it, it stays deleted;
//   2. a stored one whose id the bank no longer lists, of the same day, amount and currency and a
//      similar text → the bank gave it a new id (Lunch Flow did so for a whole account on
//      29.09.2026): update it, the new id arrives; a deleted one like that stays deleted;
//   3. a stored pending one without that id → it was booked: same amount and currency, date within
//      ±5 days, similar text → update it (the id arrives, the status becomes BOOKED);
//   4. an entry from the bank's own statement (CSV import) or added by hand for the same payment —
//      the same amount and currency within 3 days — is taken over (the bank's id arrives, nothing
//      is added twice) when its text is similar, or when it came from a CSV and is the only one;
//   5. a manual entry of the same amount    → import it, flagged "possible duplicate" for the owner;
//   6. otherwise                            → a new transaction.
// A stored pending transaction the provider no longer lists (inside the downloaded range) is not
// cancelled at once: Lunch Flow drops some while the bank still shows them (Overclockers, 30.09.2026).
// It is cancelled when a booked entry of the same merchant arrives at another amount on its day or
// later (a hold replaced by the final charge), or when it is over a week old (a card hold lapses);
// until then it stays pending, and one cancelled earlier only for not being listed comes back.

export type StoredBankTransaction = {
  readonly id: string;
  readonly externalId: string | null;
  /** Null for manual entries. */
  readonly provider: string | null;
  readonly source: string;
  readonly status: 'PENDING' | 'BOOKED' | 'REVERSED' | 'CANCELLED';
  readonly bookingDate: string;
  readonly amountMinor: bigint;
  readonly currency: string;
  readonly text: string | null;
  /** Already flagged or confirmed as the same as another entry. */
  readonly linked: boolean;
};

export type DownloadedTransaction = {
  readonly externalId: string | null;
  readonly status: 'PENDING' | 'BOOKED';
  readonly bookingDate: string;
  readonly amountMinor: bigint;
  readonly currency: string;
  readonly text: string;
};

export type ImportPlan<T extends DownloadedTransaction> = {
  readonly inserts: { readonly incoming: T; readonly possibleDuplicateOf: string | null }[];
  readonly updates: { readonly id: string; readonly incoming: T }[];
  /** Entries imported from a statement or added by hand that the bank now confirms: they become the bank's. */
  readonly adopted: { readonly id: string; readonly incoming: T }[];
  /** Pending ones that disappeared at the bank: replaced by the final charge, or a lapsed hold. */
  readonly cancelled: string[];
  /** Pending ones cancelled earlier only because the provider stopped listing them: pending again. */
  readonly reopened: string[];
  /** Downloaded transactions that are already stored exactly as they are. */
  readonly unchanged: number;
};

const DAY_MS = 86_400_000;
const daysApart = (a: string, b: string) => Math.abs(Date.parse(`${a}T00:00:00Z`) - Date.parse(`${b}T00:00:00Z`)) / DAY_MS;

const REPAID = new Set(['CREDIT_CARD', 'LOAN', 'MORTGAGE', 'LIABILITY']);

/** Days a card hold the provider stops listing is still taken as pending (holds lapse in about a week). */
const HOLD_DAYS = 7;

/** What a bank entry is, before any pairing: spending, income, a refund or a transfer. */
export function bankEntryKind(amountMinor: bigint, paymentType: string | null, accountType: string): 'EXPENSE' | 'INCOME' | 'REFUND' | 'TRANSFER' {
  if (amountMinor === 0n) return 'TRANSFER';
  if (REPAID.has(accountType)) return amountMinor < 0n ? 'EXPENSE' : paymentType === 'CARD' ? 'REFUND' : 'TRANSFER';
  if (accountType === 'SAVINGS' && paymentType !== 'CARD') return paymentType === 'INTEREST' && amountMinor > 0n ? 'INCOME' : 'TRANSFER';
  if (amountMinor < 0n) return 'EXPENSE';
  return paymentType === 'CARD' ? 'REFUND' : 'INCOME';
}

/**
 * Money coming in that the household's habits file under a spending category (an Uber Eats credit,
 * which the bank does not call a card payment) is a refund of that spending, not income.
 */
export function refundIfSpending(kind: 'EXPENSE' | 'INCOME' | 'REFUND' | 'TRANSFER', inSpendingCategory: boolean): 'EXPENSE' | 'INCOME' | 'REFUND' | 'TRANSFER' {
  return kind === 'INCOME' && inSpendingCategory ? 'REFUND' : kind;
}

/** Two bank texts for the same payment: "TESCO STORES 3321" and "TESCO STORES 3321 CD 6411". */
export function similarText(a: string | null, b: string | null): boolean {
  if (!a || !b) return false;
  const x = merchantKey(a);
  const y = merchantKey(b);
  if (!x || !y) return false;
  // A hold's text that names the merchant under another spelling: "UBR* PENDING.UBER.COM" for "UBER *TRIP".
  const names = (text: string, key: string) => key.length >= 3 && new RegExp(`\\b${key}\\b`).test(text.toLowerCase());
  return x === y || x.startsWith(y) || y.startsWith(x) || x.split(' ')[0] === y.split(' ')[0] || names(a, y) || names(b, x);
}

export function reconcileBankTransactions<T extends DownloadedTransaction>(
  stored: readonly StoredBankTransaction[],
  downloaded: readonly T[],
  options: {
    provider: string;
    from: string;
    to: string;
    unchanged?: (stored: StoredBankTransaction, incoming: T) => boolean;
    /** Bank entries the owner deleted: whatever the bank calls them now, they are not brought back. */
    deleted?: readonly StoredBankTransaction[];
  },
): ImportPlan<T> {
  const fromProvider = stored.filter((s) => s.provider === options.provider);
  const byExternal = new Map(fromProvider.filter((s) => s.externalId).map((s) => [s.externalId!, s]));
  const lapsed = (s: StoredBankTransaction) => daysApart(s.bookingDate, options.to) > HOLD_DAYS;
  // Pending, and cancelled ones still young enough to come back (only pending entries are ever cancelled).
  const openPending = fromProvider.filter((s) => s.status === 'PENDING' || (s.status === 'CANCELLED' && !lapsed(s)));
  const listed = new Set(downloaded.flatMap((d) => (d.externalId ? [d.externalId] : [])));
  // An id the bank lists no more: the entry was dropped, or it is listed again under a new id.
  const unlisted = (s: StoredBankTransaction) => s.externalId !== null && !listed.has(s.externalId);
  const renamed = (s: StoredBankTransaction, incoming: T) =>
    s.provider === options.provider &&
    unlisted(s) &&
    s.status !== 'CANCELLED' &&
    s.bookingDate === incoming.bookingDate &&
    s.amountMinor === incoming.amountMinor &&
    s.currency === incoming.currency &&
    similarText(s.text, incoming.text);
  const deleted = options.deleted ?? [];
  const deletedIds = new Set(deleted.flatMap((d) => (d.externalId ? [d.externalId] : [])));
  const claimed = new Set<string>();
  const inserts: ImportPlan<T>['inserts'][number][] = [];
  const updates: ImportPlan<T>['updates'][number][] = [];
  const adopted: ImportPlan<T>['adopted'][number][] = [];
  let unchanged = 0;

  // Booked first, so a pending entry is matched by the booked version rather than by itself.
  const ordered = [...downloaded].sort((a, b) => (a.status === b.status ? 0 : a.status === 'BOOKED' ? -1 : 1));
  for (const incoming of ordered) {
    const same = incoming.externalId ? byExternal.get(incoming.externalId) : undefined;
    if (same && !claimed.has(same.id)) {
      claimed.add(same.id);
      if (options.unchanged?.(same, incoming)) unchanged++;
      else updates.push({ id: same.id, incoming });
      continue;
    }
    if (incoming.externalId && deletedIds.has(incoming.externalId)) continue;
    const renamedFrom = fromProvider.find((s) => !claimed.has(s.id) && renamed(s, incoming));
    if (renamedFrom) {
      claimed.add(renamedFrom.id);
      updates.push({ id: renamedFrom.id, incoming });
      continue;
    }
    const renamedDeleted = deleted.find((d) => !claimed.has(d.id) && renamed(d, incoming));
    if (renamedDeleted) {
      claimed.add(renamedDeleted.id);
      continue;
    }
    const pending = openPending
      .filter(
        (p) =>
          !claimed.has(p.id) &&
          (p.externalId === null || p.externalId === incoming.externalId || unlisted(p)) &&
          p.amountMinor === incoming.amountMinor &&
          p.currency === incoming.currency &&
          daysApart(p.bookingDate, incoming.bookingDate) <= 5 &&
          (incoming.status === 'PENDING' ? p.bookingDate === incoming.bookingDate && p.text === incoming.text : similarText(p.text, incoming.text)),
      )
      .sort((a, b) => daysApart(a.bookingDate, incoming.bookingDate) - daysApart(b.bookingDate, incoming.bookingDate))[0];
    if (pending) {
      claimed.add(pending.id);
      // Listed again after being taken as cancelled: it becomes pending (or booked) once more.
      if (incoming.status === 'PENDING' && pending.status === 'PENDING') unchanged++;
      else updates.push({ id: pending.id, incoming });
      continue;
    }
    const ours = stored
      .filter(
        (s) =>
          (s.source === 'MANUAL' || s.source === 'CSV') &&
          s.provider === null &&
          !s.linked &&
          !claimed.has(s.id) &&
          s.status !== 'CANCELLED' &&
          s.amountMinor === incoming.amountMinor &&
          s.currency === incoming.currency &&
          daysApart(s.bookingDate, incoming.bookingDate) <= 3,
      )
      .sort((a, b) => daysApart(a.bookingDate, incoming.bookingDate) - daysApart(b.bookingDate, incoming.bookingDate));
    if (incoming.status === 'BOOKED') {
      const same = ours.find((s) => similarText(s.text, incoming.text)) ?? (ours.length === 1 && ours[0]!.source === 'CSV' ? ours[0] : undefined);
      if (same) {
        claimed.add(same.id);
        adopted.push({ id: same.id, incoming });
        continue;
      }
    }
    const manual = ours.find((s) => s.source === 'MANUAL');
    if (manual) claimed.add(manual.id);
    inserts.push({ incoming, possibleDuplicateOf: manual?.id ?? null });
  }

  // The final charge of a hold: a booked entry of the same merchant, on the hold's day or a few days
  // later, at another amount (at the same amount it would have taken the pending entry over above).
  const replaced = (p: StoredBankTransaction) =>
    downloaded.some((d) => d.status === 'BOOKED' && d.currency === p.currency && d.bookingDate >= p.bookingDate && daysApart(d.bookingDate, p.bookingDate) <= 5 && similarText(d.text, p.text));
  const unlistedPending = openPending.filter((p) => !claimed.has(p.id) && p.bookingDate >= options.from && p.bookingDate <= options.to);
  const cancelled = unlistedPending.filter((p) => p.status === 'PENDING' && (replaced(p) || lapsed(p))).map((p) => p.id);
  const reopened = unlistedPending.filter((p) => p.status === 'CANCELLED' && !replaced(p)).map((p) => p.id);
  return { inserts, updates, adopted, cancelled, reopened, unchanged };
}
